import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, ListItem, ListItemText, MenuItem, Select } from '@mui/material';
import { FC, Key, useEffect, useState } from 'react';
import styled from 'styled-components';
import Cancel from '@mui/icons-material/Cancel';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import { useFetchSelectedFamily } from '../components/SelectFamily/queries';
import { useFetchFamilyMembers } from '../queries/family';
import { IFamilyMember } from '../utils/withAuthorization/withAuthorization';
import { FamilyMemberCircle } from '../../modules/Dashboard/components/FamilyMembers/FamilyMemberCircle';
import Tooltip from '@mui/material/Tooltip';
import { FamilyMemberCircleWrapper } from '../../modules/Dashboard/components/FamilyMembers/FamilyMembers';
import { fullName, generateInitials } from '../utils/commonFunctions';
import { EAccountStatus } from '../constants/constants';
import { useField } from 'formik';

interface IFamilyMembersSelectorProps {
  name: string;
  multiple?: boolean;
  hideAllSelection?: boolean;
  needFiltration?: boolean;
  onlyAccepted?: boolean;
  filterFunction?: (familyMembers: IFamilyMember[]) => IFamilyMember[];
}

interface ISelectFamilyProps {
  fullWidth?: boolean;
}

const Container = styled.div`
  align-items: center;
  gap: 16px;
`;

const BlockWrapperUser = styled.div`
  display: flex;
  margin: 20px 0;
`;

const MembersRow = styled.div`
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 0 15px;
`;

const RowWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonWrapper = styled(Button)`
  margin-bottom: 20px !important;
  margin-left: 15px !important;
  padding: 10px !important;
  background: none !important;
  color: rgb(84, 124, 211) !important;
  font-weight: 600 !important;
  font-size: 16px !important;
`;

const SelectWrapper = styled(Select)`
  max-width: 200px;

  ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
    width: 200px;
  }
  
  ${ ({ theme: { breakpoints } }) => breakpoints.down('sm') } {
    width: 150px;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;

const DialogTitleWrapper = styled(DialogTitle)`
  font-size: 20px;
  text-align: center;

  .MuiTypography-h6 {
    font-size: 20px;
  }
`;

const DialogNoMembers = styled('p')`
  font-size: 18px;
  text-align: center;
`;

export const CancelIconWrapper = styled.div`
  top: 0;
  right: 0;
  width: 15px;
  height: 15px;
  position: absolute;
  border-radius: 100%;
  background-color: white;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 3px #00000036;

  svg {
    width: 18px;
    height: 18px;
    fill: red;
  }
`;

export const CancelUserIconWrapper = styled.div`
  width: 15px;
  height: 15px;
  position: absolute;
  cursor: pointer;
  left: 29px;
  border-radius: 100%;
  background-color: white;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 3px #00000036;

  svg {
    width: 18px;
    height: 18px;
    fill: red;
  }
`;

export const RemoveCircleIcon = styled(RemoveCircle)`
  width: 25px;
  height: 25px;
  fill: red;
  margin-left: 10px;
`;

export const UserRestrictionsWrapper = styled.div`
  ${ ({ theme: { breakpoints } }) => breakpoints.down('sm') } {
    width: 160px;
  }
`;

const StyledDropdown = styled(FormControl)<ISelectFamilyProps>`
  flex: 1;
  width: 100%;
  margin: 0 20px;
  max-width: 200px;
 
  .MuiSelect-outlined {
    border-radius: 9px;
    background-color: #fafafa !important;
    box-shadow: 0 0 8px 0 rgb(0 0 0 / 12%);
  }
  
  .MuiInputBase-root {
    margin-left: 20px;
    max-width: 200px;
    padding: 0;
    border-radius: 9px;
    background-color: #fafafa !important;
    box-shadow: 0 0 8px 0 rgb(0 0 0 / 12%);
    
    ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
      width: 200px;
    }
    
    ${ ({ theme: { breakpoints } }) => breakpoints.down('sm') } {
      width: 150px;
    }
  }
    .MuiInputBase-root::before, .MuiInputBase-root::after {
    border: none !important;
    }
`;

interface ISelectedUser {
  id: string;
  firstName: string;
  lastName: string;
  color: string;
  avatar?: {
    compactPath: string;
  };
}
interface IUserRestrictions {
  familyUserId: string;
  blockedFamilyUserIds: ISelectedUser[];
}

export const ChatUserSelect: FC<IFamilyMembersSelectorProps> = (props) => {
  const { data: family } = useFetchSelectedFamily({ staleTime: Infinity });
  const { data: familyMembers } = useFetchFamilyMembers(family?.id, {
    enabled   : !!family?.id,
    staleTime : Infinity,
  });

  const [ field, meta, helpers ] = useField(props.name);

  const [userRestrictions, setUserRestrictions] = useState<any[]>([]);
  const [openUserSelectModal, setOpenUserSelectModal] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);

  useEffect(() => {
    if (!field.value || !familyMembers) return;

    const updatedRestrictions = field.value.map((item: any) => ({
      familyUserId         : item.familyUserId,
      blockedFamilyUserIds : familyMembers
        .filter(m => item.blockedFamilyUserIds?.includes(m.id))
        .map(m => ({
          id        : m.id,
          firstName : m.firstName,
          lastName  : m.lastName,
          color     : m.color,
          avatar    : m.avatar,
        })),
    }));

    setUserRestrictions(updatedRestrictions);

    const backendFormat = updatedRestrictions.map((item: { familyUserId: any; blockedFamilyUserIds: { id: any }[] }) => ({
      familyUserId         : item.familyUserId,
      blockedFamilyUserIds : item.blockedFamilyUserIds.map((u: { id: any }) => u.id),
    }));
    helpers.setValue(backendFormat);

    const selected = updatedRestrictions
      .map((r: { familyUserId: string }) => familyMembers.find(m => m.id === r.familyUserId))
      .filter(Boolean)
      .map((m: any) => ({
        id        : m!.id,
        firstName : m!.firstName,
        lastName  : m!.lastName,
        color     : m!.color,
        avatar    : m!.avatar,
      }));
    setSelectedMembers(selected);
  }, [familyMembers]);

  useEffect(() => {
    setUserRestrictions(field.value);
  }, []);

  useEffect(() => {
    helpers.setValue(userRestrictions);
  }, [userRestrictions, helpers]);

  useEffect(() => {
    setUserRestrictions(field.value);
  }, []);

  const handleUserSelect = (familyUserId: string, selectedUsers: ISelectedUser[]) => {
    if (!familyUserId) return;

    const updatedRestrictions = userRestrictions.filter(item => item.familyUserId !== familyUserId);
    if (selectedUsers.length > 0) {
      updatedRestrictions.push({ familyUserId, blockedFamilyUserIds: selectedUsers });
    }

    setUserRestrictions(updatedRestrictions);

    const backendFormat = updatedRestrictions.map(item => ({
      familyUserId         : item?.familyUserId,
      blockedFamilyUserIds : item?.blockedFamilyUserIds.map((u: { id: any }) => u.id),
    }));

    helpers.setValue(backendFormat);
  };

  const handleRemoveBlockedUser = (familyUserId: string, blockedUserId: string) => {
    setUserRestrictions(prev =>
      prev.map(restriction => {
        if (restriction.familyUserId === familyUserId) {
          return {
            ...restriction,
            blockedFamilyUserIds: restriction.blockedFamilyUserIds.filter(
              (user: ISelectedUser) => user.id !== blockedUserId
            ),
          };
        }
        return restriction;
      })
    );
  };

  const handleRemoveSelectedMember = (memberId: string) => {
    setSelectedMembers(prev => prev.filter(member => member.id !== memberId));

    setUserRestrictions(prev => prev.filter(restriction => restriction.familyUserId !== memberId));
  };

  const handleOpenRestrictionsModal = (member: any) => {
    setSelectedMembers(prev =>
      prev.some(m => m.id === member.id) ? prev : [...prev, member]
    );
  };

  return (
    <Wrapper>
      <Container>
        <Dialog open={openUserSelectModal} onClose={() => setOpenUserSelectModal(false)}>
          <DialogTitleWrapper>Select family member to restrict</DialogTitleWrapper>
          <DialogContent>
            {familyMembers && familyMembers.filter(member => member.accountType !== "SuperAdmin").length > 0
              ? familyMembers
                .filter(member => member.accountType !== "SuperAdmin")
                .map((member) => (
                  <ListItem
                    key={member.id}
                    button
                    onClick={() => {
                      handleOpenRestrictionsModal(member);
                      setOpenUserSelectModal(false);
                    }}
                  >
                    <FamilyMemberCircle
                      color={member.color}
                      image={member.avatar?.compactPath}
                      initials={generateInitials(member.firstName, member.lastName)}
                    />
                    <ListItemText
                      primary={fullName(member.firstName, member.lastName)}
                      style={{ marginLeft: '10px' }}
                    />
                  </ListItem>
                ))
              : (
                <DialogNoMembers>
                  No family members found. Please add a member first.
                </DialogNoMembers>
              )}

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenUserSelectModal(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <MembersRow>
          { selectedMembers?.map((member, index) => (
            <RowWrapper key={index}>
              <Tooltip
                key={index}
                title={fullName(member?.firstName, member?.lastName)}
                PopperProps={{ modifiers: [
                        {
                            name    : 'offset',
                            options : {
                                offset: [0, -85], // offset is a tuple [skidding, distance]
                            },
                        },
                    ] }}
              >
                <FamilyMemberCircleWrapper>
                  <BlockWrapperUser>
                    <FamilyMemberCircle
                      color={member?.color}
                      image={member?.avatar?.compactPath}
                      pending={member?.status === EAccountStatus.Pending}
                      initials={generateInitials(member?.firstName, member?.lastName)}
                    />

                    <CancelUserIconWrapper onClick={() => handleRemoveSelectedMember(member.id)}>
                      <Cancel />
                    </CancelUserIconWrapper>

                    <StyledDropdown
                      fullWidth
                      variant="outlined"
                    >
                      <SelectWrapper
                        multiple
                        variant="standard"
                        value={
                          userRestrictions.find(r => r.familyUserId === member.id)?.blockedFamilyUserIds?.map((u: any) => {
                            if (!u) return null;
                            return typeof u === 'string' ? u : u.id;
                          }).filter(Boolean) || []
                        }
                        renderValue={(selected) => {
                          const selectedIds = Array.isArray(selected) ? selected : [];

                          if (selectedIds.length === 0) {
                            return <span style={{ color: '#888' }}>Cannot start chat with</span>;
                          }

                          const matchedUsers = familyMembers?.filter(m => selectedIds.includes(m.id)) || [];
                          if (matchedUsers.length === 0) {
                            return <span style={{ color: '#888' }}>Cannot start chat with</span>;
                          }

                          const initials = matchedUsers
                            .map(user => generateInitials(user.firstName, user.lastName))
                            .join(', ');

                          return <span>{initials}</span>;
                        }}
                        onChange={(e) => {
                          const selectedIds = e.target.value as string[];
                          const selectedUsersData = familyMembers
                            ?.filter(m => selectedIds.includes(m.id))
                            ?.map(m => ({
                              id        : m.id,
                              firstName : m.firstName,
                              lastName  : m.lastName,
                              color     : m.color,
                              avatar    : m.avatar,
                            })) || [];

                          handleUserSelect(member.id, selectedUsersData);
                        }}
                      >
                        <MenuItem disabled value="select">
                          <em>Select family members</em>
                        </MenuItem>

                        {familyMembers
                          ?.filter(item =>
                            item.id !== member.id && item.accountType !== 'SuperAdmin'
                          )
                          ?.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              <div>{fullName(item?.firstName, item?.lastName)}</div>
                            </MenuItem>
                          ))}
                      </SelectWrapper>
                    </StyledDropdown>

                    <UserRestrictionsWrapper style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {userRestrictions
                        .find(r => r.familyUserId === member.id)
                        ?.blockedFamilyUserIds
                        ?.map((blockedId: any) => {
                          const blockedUser = familyMembers?.find(m => m.id === blockedId?.id);
                          if (!blockedUser) return null;

                          return (
                            <Tooltip key={blockedUser.id} title={fullName(blockedUser.firstName, blockedUser.lastName)}>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <FamilyMemberCircle
                                  color={blockedUser.color}
                                  image={blockedUser.avatar?.compactPath}
                                  initials={generateInitials(blockedUser.firstName, blockedUser.lastName)}
                                />
                                <CancelIconWrapper onClick={() => handleRemoveBlockedUser(member.id, blockedUser.id)}>
                                  <Cancel />
                                </CancelIconWrapper>
                              </div>
                            </Tooltip>
                          );
                        })}
                    </UserRestrictionsWrapper>
                  </BlockWrapperUser>
                </FamilyMemberCircleWrapper>
              </Tooltip>
            </RowWrapper>
          )) }
        </MembersRow>

        <ButtonWrapper
          variant="contained"
          style={{ marginBottom: '20px' }}
          onClick={() => setOpenUserSelectModal(true)}
        >
          + Create New Chat Restriction
        </ButtonWrapper>
      </Container>
    </Wrapper>
  );
};
